import type { SVGProps } from 'react';

/**
 * Animated eIDAS verification flow illustration.
 *
 * Shows the full 3-step journey: QR scan → EU wallet auth → success.
 * Loops every 10 seconds. Uses Electric Europa palette and the project's
 * DM Serif Display / Outfit typefaces (inherited from the host page).
 *
 * Follows the same SVGProps convention as Logo — accepts width, height,
 * className, style, aria-* etc. Default dimensions match the 400 × 300 viewBox.
 *
 * @example
 * // Default size
 * <VerificationFlow />
 *
 * // Responsive (fill container)
 * <VerificationFlow width="100%" height="auto" />
 *
 * // Custom dimensions
 * <VerificationFlow width={800} height={600} />
 *
 * // Override background (e.g. transparent embed)
 * <VerificationFlow style={{ backgroundColor: 'transparent' }} />
 */
// eslint-disable-next-line no-undef
export function VerificationFlow({ width = 400, height = 300, style, ...props }: SVGProps<SVGSVGElement>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 400 300"
            width={width}
            height={height}
            role="img"
            aria-label="eIDAS verification flow animation"
            style={{ backgroundColor: '#f0f4f8', fontFamily: "'Outfit', system-ui, sans-serif", ...style }}
            {...props}>
            <defs>
                {/* ── QR code symbol (reused on desktop + phone screens) ──────── */}
                <g id="vf-the-qr">
                    <g id="vf-finder">
                        <rect x="0" y="0" width="28" height="28" fill="#172B4D" />
                        <rect x="4" y="4" width="20" height="20" fill="#fff" />
                        <rect x="8" y="8" width="12" height="12" fill="#172B4D" />
                    </g>
                    <use href="#vf-finder" x="0" y="0" />
                    <use href="#vf-finder" x="56" y="0" />
                    <use href="#vf-finder" x="0" y="56" />
                    <rect x="60" y="60" width="16" height="16" fill="#172B4D" />
                    <rect x="64" y="64" width="8" height="8" fill="#fff" />
                    <rect x="66" y="66" width="4" height="4" fill="#172B4D" />
                    <path
                        d="M32,0h8v8h-8z M44,4h12v4h-12z M32,20h20v8h-20z M52,20h4v4h-4z
                           M0,32h8v8h-8z M4,44h4v12h-4z M20,32h8v20h-8z M20,52h4v4h-4z
                           M32,32h12v12h-12z M48,32h8v8h-8z M60,32h24v8h-24z M32,48h8v24h-8z
                           M44,48h20v8h-20z M68,44h16v12h-16z M44,60h12v12h-12z M44,76h24v8h-24z
                           M72,80h12v4h-12z M32,76h8v8h-8z M80,60h4v16h-4z M20,20h8v8h-8z
                           M56,36h4v16h-4z M32,12h4v4h-4z M40,12h4v4h-4z M48,12h4v4h-4z
                           M12,32h4v4h-4z M12,40h4v4h-4z M12,48h4v4h-4z"
                        fill="#172B4D"
                    />
                </g>

                {/* ── Trust Navy → EU Blue vertical gradient ─────────────────── */}
                <linearGradient id="vf-euGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#172B4D" stopOpacity={1} />
                    <stop offset="100%" stopColor="#0052CC" stopOpacity={1} />
                </linearGradient>

                {/* ── Drop shadow filter for phone ───────────────────────────── */}
                <filter id="vf-shadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="4" />
                    <feOffset dx="0" dy="6" result="offsetblur" />
                    <feComponentTransfer>
                        <feFuncA type="linear" slope="0.25" />
                    </feComponentTransfer>
                    <feMerge>
                        <feMergeNode />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>

            {/* ── Animation stylesheet ──────────────────────────────────────── */}
            <style>{`
                /* 10-second master loop */
                #vf-phone {
                    animation: vf-phoneSlide 10s cubic-bezier(0.25, 1, 0.5, 1) infinite;
                    transform-origin: center;
                }
                #vf-scan-line {
                    animation: vf-scanMove 10s ease-in-out infinite;
                    opacity: 0;
                }
                #vf-screen-camera { animation: vf-fadeCamera 10s step-end infinite; }
                #vf-screen-wallet  { opacity: 0; animation: vf-fadeWallet  10s step-end infinite; }
                #vf-screen-success { opacity: 0; animation: vf-fadeSuccess 10s step-end infinite; }
                #vf-wallet-spinner {
                    transform-origin: 200px 150px;
                    animation: vf-spin 1.5s linear infinite;
                }
                #vf-id-card {
                    transform-origin: 200px 150px;
                    animation: vf-cardInsert 10s cubic-bezier(0.34, 1.56, 0.64, 1) infinite;
                    opacity: 0;
                }
                #vf-check-circle {
                    stroke-dasharray: 100;
                    stroke-dashoffset: 100;
                    animation: vf-drawCheckCircle 10s ease-out infinite;
                }
                #vf-check-mark {
                    stroke-dasharray: 50;
                    stroke-dashoffset: 50;
                    animation: vf-drawCheckMark 10s ease-out infinite;
                }

                /* Phone entrance / exit */
                @keyframes vf-phoneSlide {
                    0%,   5% { transform: translateY(350px); }
                    15%, 85% { transform: translateY(0px);   }
                    95%, 100%{ transform: translateY(350px); }
                }

                /* Scan-line sweep */
                @keyframes vf-scanMove {
                    0%,  15% { opacity: 0; transform: translateY(0);     }
                    16%      { opacity: 1; transform: translateY(0);     }
                    28%      {             transform: translateY(110px); opacity: 1; }
                    40%      {             transform: translateY(0);     opacity: 1; }
                    41%, 100%{ opacity: 0; transform: translateY(0);     }
                }

                /* Screen cross-fades */
                @keyframes vf-fadeCamera  { 0%,  41% { opacity: 1; } 42%, 100% { opacity: 0; } }
                @keyframes vf-fadeWallet  { 0%,  41% { opacity: 0; } 42%,  71% { opacity: 1; } 72%, 100% { opacity: 0; } }
                @keyframes vf-fadeSuccess { 0%,  71% { opacity: 0; } 72%, 100% { opacity: 1; } }

                /* Spinner & card */
                @keyframes vf-spin { 100% { transform: rotate(360deg); } }
                @keyframes vf-cardInsert {
                    38%, 41% { transform: translateY(60px) scale(0.7); opacity: 0; }
                    45%, 68% { transform: translateY(0px)  scale(1);   opacity: 1; }
                    72%, 100%{ opacity: 0; transform: translateY(0px) scale(1); }
                }

                /* Check circle draw-on */
                @keyframes vf-drawCheckCircle {
                    0%,  71% { stroke-dashoffset: 100; }
                    76%, 100%{ stroke-dashoffset: 0;   }
                }
                @keyframes vf-drawCheckMark {
                    0%,  75% { stroke-dashoffset: 50; }
                    80%, 100%{ stroke-dashoffset: 0;  }
                }
            `}</style>

            {/* ── Desktop monitor ───────────────────────────────────────────── */}
            <g transform="translate(85, 40)">
                {/* Stand */}
                <path d="M100,160 L130,160 L120,195 L110,195 Z" fill="#b0b8c0" />
                <rect x="80" y="195" width="70" height="8" rx="3" fill="#9098a0" />

                {/* Frame — Trust Navy */}
                <rect x="0" y="0" width="230" height="160" rx="8" fill="#172B4D" />
                {/* Screen */}
                <rect x="5" y="5" width="220" height="150" rx="4" fill="#ffffff" />

                {/* Browser chrome — EU Blue */}
                <rect x="5" y="5" width="220" height="30" fill="#0052CC" />
                <circle cx="18" cy="20" r="3.5" fill="rgba(255,255,255,0.3)" />
                <circle cx="29" cy="20" r="3.5" fill="rgba(255,255,255,0.3)" />
                <circle cx="40" cy="20" r="3.5" fill="rgba(255,255,255,0.3)" />
                {/*
                    Title: centered in the space right of the browser dots.
                    DM Serif Display → institutional serif matching landing page h2/h3.
                */}
                <text
                    x="140"
                    y="24"
                    textAnchor="middle"
                    fontSize="12"
                    fill="white"
                    fontWeight="600"
                    letterSpacing="1.5"
                    fontFamily="'DM Serif Display', Georgia, serif">
                    eIDAS 2.0 PORTAL
                </text>

                {/*
                    QR card — translate(65, 44) with 96×96 padding rect.
                    Card top  = 44 − 6 = 38px  (3 px below header bar at y=35)  ✓
                    Card bottom = 44 − 6 + 96 = 134px
                    Label at y=146: 12 px below card, 9 px above screen bottom (155)  ✓
                */}
                <g transform="translate(65, 44)">
                    <rect x="-6" y="-6" width="96" height="96" rx="5" fill="white" stroke="#e2e8f0" strokeWidth="1.5" />
                    <use href="#vf-the-qr" />
                </g>

                {/* Label — Trust Slate, Outfit, clear of QR card */}
                <text
                    x="115"
                    y="146"
                    textAnchor="middle"
                    fontSize="9.5"
                    fill="#253858"
                    letterSpacing="0.3"
                    fontFamily="'Outfit', system-ui, sans-serif">
                    Scan with your EUDI Wallet
                </text>
            </g>

            {/* ── Phone ─────────────────────────────────────────────────────── */}
            <g id="vf-phone" filter="url(#vf-shadow)">
                <rect x="130" y="45" width="140" height="245" rx="22" fill="#1a1a1c" stroke="#333" strokeWidth="2" />

                <defs>
                    <clipPath id="vf-screenClip">
                        <rect x="136" y="51" width="128" height="233" rx="16" />
                    </clipPath>
                </defs>

                <g clipPath="url(#vf-screenClip)">
                    {/* ── Screen 1: Camera / QR scanning ─────────────────────── */}
                    <g id="vf-screen-camera">
                        <rect x="136" y="51" width="128" height="233" fill="#0d0d0d" />

                        {/* QR rendered on phone screen */}
                        <g transform="translate(158, 82)">
                            <rect x="-4" y="-4" width="92" height="92" fill="#fff" rx="4" />
                            <use href="#vf-the-qr" />
                        </g>

                        {/*
                            Corner brackets — EU Blue.
                            Bottom corners at y=182.
                            "Align QR Code" at y=208 → 26 px gap  ✓
                        */}
                        <path
                            d="M165,72 L150,72 L150,87 M235,72 L250,72 L250,87 M165,182 L150,182 L150,167 M235,182 L250,182 L250,167"
                            stroke="#0052CC"
                            strokeWidth="3"
                            fill="none"
                            strokeLinecap="round"
                        />

                        {/* Animated scan line */}
                        <line id="vf-scan-line" x1="147" y1="72" x2="253" y2="72" stroke="#0052CC" strokeWidth="2.5">
                            <animate
                                attributeName="stroke-opacity"
                                values="0.4;1;0.4"
                                dur="0.6s"
                                repeatCount="indefinite"
                            />
                        </line>

                        {/* Label — 26 px below bracket bottom */}
                        <text
                            x="200"
                            y="208"
                            textAnchor="middle"
                            fontSize="11"
                            fill="white"
                            fontWeight="600"
                            fontFamily="'Outfit', system-ui, sans-serif"
                            letterSpacing="0.5">
                            Align QR Code
                        </text>
                        <rect x="176" y="220" width="48" height="3" rx="1.5" fill="white" opacity="0.25" />
                    </g>

                    {/* ── Screen 2: EU Wallet authentication ─────────────────── */}
                    <g id="vf-screen-wallet">
                        <rect x="136" y="51" width="128" height="233" fill="url(#vf-euGradient)" />

                        {/*
                            Header at y=83 — 32 px from screen top (y=51)  ✓
                            Spinner bottom y=184, "Authenticating..." at y=210 → 26 px gap  ✓
                        */}
                        <text
                            x="200"
                            y="83"
                            textAnchor="middle"
                            fontSize="12"
                            fill="white"
                            fontWeight="700"
                            fontFamily="'Outfit', system-ui, sans-serif"
                            letterSpacing="0.5">
                            EU Digital Identity
                        </text>
                        <line x1="158" y1="92" x2="242" y2="92" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />

                        {/* Spinner ring */}
                        <g id="vf-wallet-spinner">
                            <circle
                                cx="200"
                                cy="150"
                                r="34"
                                stroke="rgba(255,255,255,0.18)"
                                strokeWidth="4"
                                fill="none"
                                strokeDasharray="12 12"
                            />
                        </g>

                        {/* Animated ID card */}
                        <g id="vf-id-card" transform="translate(200, 150)">
                            <rect x="-26" y="-18" width="52" height="36" rx="4" fill="#ffffff" />
                            <rect x="-26" y="-18" width="52" height="8" rx="4" fill="#0052CC" />
                            <rect x="-26" y="-14" width="52" height="4" fill="#0052CC" />
                            <rect x="-20" y="-5" width="12" height="14" rx="2" fill="#cbd5e1" />
                            <line
                                x1="-2"
                                y1="-2"
                                x2="18"
                                y2="-2"
                                stroke="#94a3b8"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                            />
                            <line
                                x1="-2"
                                y1="6"
                                x2="12"
                                y2="6"
                                stroke="#94a3b8"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                            />
                        </g>

                        {/* Status label */}
                        <text
                            x="200"
                            y="210"
                            textAnchor="middle"
                            fontSize="11"
                            fill="rgba(179,210,255,0.9)"
                            fontWeight="500"
                            fontFamily="'Outfit', system-ui, sans-serif"
                            letterSpacing="0.3">
                            Authenticating...
                        </text>
                    </g>

                    {/* ── Screen 3: Success ───────────────────────────────────── */}
                    <g id="vf-screen-success">
                        <rect x="136" y="51" width="128" height="233" fill="#00C853" />

                        {/*
                            Circle centre y=140, r=32 → bottom y=172.
                            "Verified" at y=195 → 23 px gap  ✓
                            Subtitle  at y=212 → 17 px below heading  ✓
                        */}
                        <circle cx="200" cy="140" r="32" fill="white" />
                        <g transform="translate(200, 140)">
                            <circle
                                id="vf-check-circle"
                                r="26"
                                fill="none"
                                stroke="#00C853"
                                strokeWidth="4"
                                transform="rotate(-90)"
                            />
                            <path
                                id="vf-check-mark"
                                d="M-12,2 L-4,10 L14,-8"
                                fill="none"
                                stroke="#00C853"
                                strokeWidth="5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </g>

                        {/* "Verified" — DM Serif Display for institutional elegance */}
                        <text
                            x="200"
                            y="195"
                            textAnchor="middle"
                            fontSize="17"
                            fill="white"
                            fontWeight="400"
                            fontFamily="'DM Serif Display', Georgia, serif"
                            letterSpacing="0.5">
                            Verified
                        </text>
                        <text
                            x="200"
                            y="212"
                            textAnchor="middle"
                            fontSize="9.5"
                            fill="rgba(255,255,255,0.9)"
                            fontFamily="'Outfit', system-ui, sans-serif"
                            letterSpacing="0.8">
                            Secure Login Complete
                        </text>
                    </g>
                </g>

                {/* Notch */}
                <path d="M185,51 L215,51 L215,60 Q215,65 210,65 L190,65 Q185,65 185,60 Z" fill="#1a1a1c" />
                {/* Home indicator */}
                <rect x="180" y="276" width="40" height="3" rx="1.5" fill="#ffffff" opacity="0.5" />
            </g>
        </svg>
    );
}
